import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {BrandGuidelines} from 'common/src/modules/brandGuidelines/brandGuidelines.types';
import {BrandGuidelinesState} from './brandGuidelines.types';

const initialState: BrandGuidelinesState = {
  data: null,
  localData: null,
  loading: false,
  error: null,
  UnsavedDataInUIForm: false,
};

// Helper function to compute if there are unsaved changes
const computeUnsavedChanges = (data: BrandGuidelines | null, localData: BrandGuidelines | null): boolean => {
  // If no local data, there are no unsaved changes (not editing)
  if (!localData) return false;
  
  // If local data exists but no DB data, there are unsaved changes (new data to save)
  if (!data) return true;
  
  // Both exist, compare them
  return JSON.stringify(data) !== JSON.stringify(localData);
};

const brandGuidelinesSlice = createSlice({
  name: 'brandGuidelines',
  initialState,
  reducers: {
    clearBrandGuidelines: (state) => {
      state.data = null;
      state.localData = null;
      state.UnsavedDataInUIForm = false;
      state.error = null;
    },
    updateBrandGuidelines: (state, action: PayloadAction<BrandGuidelines>) => {
      state.data = action.payload;
      state.UnsavedDataInUIForm = computeUnsavedChanges(state.data, state.localData);
    },
    // Update local data (used by forms, agent, and initialization)
    updateLocalBrandGuidelines: (state, action: PayloadAction<BrandGuidelines>) => {
      state.localData = action.payload;
      state.UnsavedDataInUIForm = computeUnsavedChanges(state.data, state.localData);
    },
    // Clear local data (when leaving page)
    clearLocalBrandGuidelines: (state) => {
      state.localData = null;
      state.UnsavedDataInUIForm = false;
    },

    setUnsavedDataInUIForm: (state, action: PayloadAction<boolean>) => {
      state.UnsavedDataInUIForm = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  clearBrandGuidelines,
  updateBrandGuidelines,
  updateLocalBrandGuidelines,
  clearLocalBrandGuidelines,
  setUnsavedDataInUIForm,
  setLoading,
  setError,
} = brandGuidelinesSlice.actions;

export default brandGuidelinesSlice.reducer; 